import React, { useCallback, useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";

const ACCENT = "#00A396";
const MUTED = "#7A8490";
const LINE = "#E3E7EB";
const OK_COLOR = "#1F8A4C";
const FIX_COLOR = "#C2571F";

const WEEKDAY_LIST = ["일", "월", "화", "수", "목", "금", "토"];

const formatDay = (dateText) => {
  const [year, month, day] = dateText.split("-").map(Number);
  const weekday = WEEKDAY_LIST[new Date(year, month - 1, day).getDay()];
  return `${month}월 ${day}일 (${weekday})`;
};

const statusColor = (status) =>
  status === "ok" ? OK_COLOR : status === "fix" ? FIX_COLOR : LINE;

const ReviewControls = ({ review, onSave }) => {
  const [memo, setMemo] = useState(review.memo || "");

  useEffect(() => {
    setMemo(review.memo || "");
  }, [review.memo]);

  const statusButton = (value, label, color) => {
    const pressed = review.status === value;
    return (
      <button
        type="button"
        aria-pressed={pressed}
        onClick={() => onSave({ status: pressed ? "" : value })}
        style={{
          flex: 1,
          padding: "8px 0",
          borderRadius: "6px",
          border: `1px solid ${pressed ? color : LINE}`,
          background: pressed ? color : "#fff",
          color: pressed ? "#fff" : MUTED,
          fontWeight: 700,
          fontSize: "0.82rem",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <div style={{ display: "flex", gap: "6px" }}>
        {statusButton("ok", "확인", OK_COLOR)}
        {statusButton("fix", "수정 요청", FIX_COLOR)}
      </div>
      <textarea
        value={memo}
        placeholder="고칠 점"
        rows={2}
        onChange={(event) => setMemo(event.target.value)}
        onBlur={() => {
          if (memo.trim() !== (review.memo || "")) onSave({ memo });
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          resize: "vertical",
          padding: "8px",
          border: `1px solid ${LINE}`,
          borderRadius: "6px",
          fontSize: "0.85rem",
          fontFamily: "inherit",
        }}
      />
    </>
  );
};

const PRIVACY_OPTION_LIST = [
  { value: "private", label: "비공개" },
  { value: "unlisted", label: "일부 공개" },
  { value: "public", label: "공개" },
];

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px",
  border: `1px solid ${LINE}`,
  borderRadius: "6px",
  fontSize: "0.85rem",
  fontFamily: "inherit",
};

const fieldLabelStyle = { fontSize: "0.78rem", color: MUTED, fontWeight: 700 };

const YoutubeUploadPanel = ({ youtube, upload, canUpload, onUpload }) => {
  const [title, setTitle] = useState(youtube.title);
  const [description, setDescription] = useState(youtube.description);
  const [privacy, setPrivacy] = useState(youtube.privacy || "public");
  const uploading = upload?.status === "uploading";

  return (
    <details style={{ fontSize: "0.82rem" }}>
      <summary style={{ cursor: "pointer", color: MUTED, fontWeight: 700 }}>
        유튜브
        {upload?.status === "done" && <span style={{ marginLeft: "6px", color: OK_COLOR }}>올림</span>}
        {uploading && <span style={{ marginLeft: "6px", color: ACCENT }}>올리는 중</span>}
        {upload?.status === "failed" && <span style={{ marginLeft: "6px", color: FIX_COLOR }}>실패</span>}
      </summary>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
        <label style={fieldLabelStyle}>제목</label>
        <input value={title} maxLength={100} onChange={(event) => setTitle(event.target.value)} style={fieldStyle} />
        <label style={fieldLabelStyle}>설명</label>
        <textarea
          value={description}
          rows={6}
          onChange={(event) => setDescription(event.target.value)}
          style={{ ...fieldStyle, resize: "vertical" }}
        />
        <div style={{ color: MUTED, lineHeight: 1.5 }}>{youtube.tag_list.join(", ")}</div>
        <label style={fieldLabelStyle}>공개 범위</label>
        <select value={privacy} onChange={(event) => setPrivacy(event.target.value)} style={fieldStyle}>
          {PRIVACY_OPTION_LIST.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {upload?.youtube_url && (
          <a href={upload.youtube_url} target="_blank" rel="noreferrer" style={{ color: ACCENT, fontWeight: 700 }}>
            {upload.youtube_url}
          </a>
        )}
        {upload?.error && <div style={{ color: FIX_COLOR, wordBreak: "break-all" }}>{upload.error}</div>}
        <button
          type="button"
          onClick={() => {
            if (!canUpload) {
              alert("유튜브 채널을 먼저 연결해 주세요.");
              return;
            }
            if (uploading) return;
            if (upload?.status === "done" && !window.confirm("이미 올린 영상입니다. 한 번 더 올리시겠습니까?")) return;
            onUpload({ title, description, privacy, tag_list: youtube.tag_list });
          }}
          style={{
            padding: "10px 0",
            borderRadius: "6px",
            border: "none",
            background: uploading ? LINE : "#F5B400",
            color: "#0E141B",
            fontWeight: 800,
            fontSize: "0.88rem",
            cursor: uploading ? "default" : "pointer",
          }}
        >
          {uploading ? "올리는 중..." : "올리기"}
        </button>
      </div>
    </details>
  );
};

const VideoCard = ({ item, shape, review, onSave, onDelete, upload, canUpload, onUpload }) => (
  <article
    style={{
      position: "relative",
      background: "#fff",
      border: `1px solid ${statusColor(review.status)}`,
      borderRadius: "10px",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    }}
  >
    <button
      type="button"
      aria-label="지우기"
      onClick={onDelete}
      style={{
        position: "absolute",
        top: "8px",
        right: "8px",
        zIndex: 1,
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        border: "none",
        background: "rgba(14, 20, 27, 0.65)",
        color: "#fff",
        fontSize: "1rem",
        lineHeight: 1,
        cursor: "pointer",
      }}
    >
      ✕
    </button>
    <video
      src={item.url}
      poster={item.thumb}
      controls
      preload="none"
      playsInline
      style={{
        display: "block",
        width: "100%",
        aspectRatio: shape === "tall" ? "9 / 16" : "16 / 9",
        background: "#000",
        objectFit: "contain",
      }}
    />
    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
      <div style={{ fontWeight: 800, fontSize: shape === "tall" ? "0.88rem" : "1rem", lineHeight: 1.35 }}>
        {item.name}
        <span style={{ marginLeft: "6px", color: MUTED, fontWeight: 500, fontSize: "0.8rem" }}>
          {item.duration}
        </span>
      </div>
      {item.sub && <div style={{ color: MUTED, fontSize: "0.82rem" }}>{item.sub}</div>}
      {item.old_list?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", fontSize: "0.78rem" }}>
          {item.old_list.map((old) => (
            <a key={old.url} href={old.url} target="_blank" rel="noreferrer" style={{ color: MUTED }}>
              {old.label}
            </a>
          ))}
        </div>
      )}
      {item.youtube && (
        <YoutubeUploadPanel youtube={item.youtube} upload={upload} canUpload={canUpload} onUpload={onUpload} />
      )}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
        <ReviewControls review={review} onSave={onSave} />
      </div>
    </div>
  </article>
);

const LinkCard = ({ link, review, onSave }) => (
  <article
    style={{
      background: "#fff",
      border: `1px solid ${statusColor(review.status)}`,
      borderRadius: "10px",
      padding: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      minWidth: 0,
    }}
  >
    <a
      href={link.url}
      target="_blank"
      rel="noreferrer"
      style={{ fontWeight: 800, color: ACCENT, textDecoration: "none" }}
    >
      {link.name}
    </a>
    <div style={{ color: MUTED, fontSize: "0.82rem" }}>{link.sub}</div>
    <div style={{ fontSize: "0.75rem", color: MUTED, wordBreak: "break-all" }}>{link.url}</div>
    <ReviewControls review={review} onSave={onSave} />
  </article>
);

const PromoReviewPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    apiClient
      .get("/api/v1/echo/admin/promo-review", { withAuth: true })
      .then((response) => setData(response.data))
      .catch((caught) =>
        setError(caught.response?.data?.error || "검토 목록을 불러오지 못했습니다.")
      );
  }, []);

  useEffect(load, [load]);

  const [youtubeState, setYoutubeState] = useState(null);

  const loadYoutube = useCallback(() => {
    apiClient
      .get("/api/v1/echo/admin/youtube", { withAuth: true })
      .then((response) => setYoutubeState(response.data))
      .catch(() => setYoutubeState(null));
  }, []);

  useEffect(loadYoutube, [loadYoutube]);

  // 구글 동의 화면에서 돌아오면 ?youtube=connected 처럼 결과가 붙어 온다
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("youtube");
    if (!result) return;
    const messageByResult = {
      connected: "유튜브 채널을 연결했습니다.",
      denied: "구글에서 허락하지 않아 연결하지 못했습니다.",
      invalid: "연결 요청이 만료됐습니다. 다시 눌러 주세요.",
      failed: "구글 연결에 실패했습니다. 다시 눌러 주세요.",
    };
    alert(messageByResult[result] || "유튜브 연결 결과를 알 수 없습니다.");
    params.delete("youtube");
    const query = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : ""));
  }, []);

  const hasUploading = Object.values(youtubeState?.upload_map || {}).some((upload) => upload.status === "uploading");
  useEffect(() => {
    if (!hasUploading) return undefined;
    const timer = setInterval(loadYoutube, 5000);
    return () => clearInterval(timer);
  }, [hasUploading, loadYoutube]);

  const connectYoutube = () => {
    apiClient
      .get("/api/v1/echo/admin/youtube/connect", { withAuth: true })
      .then((response) => {
        window.location.href = response.data.authorization_url;
      })
      .catch((caught) => alert(caught.response?.data?.error || "연결 주소를 받지 못했습니다."));
  };

  const uploadToYoutube = (itemKey) => (form) => {
    apiClient
      .post("/api/v1/echo/admin/youtube", { item_key: itemKey, ...form }, { withAuth: true })
      .then(loadYoutube)
      .catch((caught) => alert(caught.response?.data?.error || "올리지 못했습니다."));
  };

  const saveReview = (itemKey) => (change) => {
    setData((previous) => ({
      ...previous,
      review_map: {
        ...previous.review_map,
        [itemKey]: { ...(previous.review_map[itemKey] || {}), ...change },
      },
    }));
    apiClient
      .post("/api/v1/echo/admin/promo-review", { item_key: itemKey, ...change }, { withAuth: true })
      .then((response) =>
        setData((previous) => ({
          ...previous,
          review_map: { ...previous.review_map, [itemKey]: response.data },
        }))
      )
      .catch((caught) => {
        alert(caught.response?.data?.error || "저장하지 못했습니다.");
        load();
      });
  };

  const deleteItem = (itemKey) => () => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    apiClient
      .post("/api/v1/echo/admin/promo-review", { item_key: itemKey, delete: true }, { withAuth: true })
      .then(load)
      .catch((caught) => alert(caught.response?.data?.error || "지우지 못했습니다."));
  };

  if (error) return <p style={{ padding: "26px" }}>{error}</p>;
  if (!data) return <p style={{ padding: "26px" }}>불러오는 중...</p>;

  const reviewOf = (key) => data.review_map[key] || {};
  const keyList = [
    ...data.link_list.map((link) => link.key),
    ...data.day_list.flatMap((day) =>
      day.group_list.flatMap((group) => group.item_list.map((item) => item.key))
    ),
  ];
  const okCount = keyList.filter((key) => reviewOf(key).status === "ok").length;
  const fixCount = keyList.filter((key) => reviewOf(key).status === "fix").length;

  const chip = (label, color) => (
    <span
      style={{
        fontSize: "0.8rem",
        fontWeight: 700,
        padding: "4px 10px",
        borderRadius: "999px",
        border: `1px solid ${color}`,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );

  const gridStyle = (shape) => ({
    display: "grid",
    gap: "14px",
    gridTemplateColumns: `repeat(auto-fill, minmax(${shape === "tall" ? "170px" : "290px"}, 1fr))`,
  });

  const sectionHead = (title, extra) => (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "10px",
        borderBottom: "2px solid #0E141B",
        paddingBottom: "6px",
        margin: "34px 0 12px",
      }}
    >
      <h3 style={{ fontSize: "1.1rem", fontWeight: 800, margin: 0 }}>{title}</h3>
      {extra}
    </div>
  );

  return (
    <div style={{ padding: "22px 16px 60px", maxWidth: "1180px", margin: "0 auto" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 16px" }}>
        <h2 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0 }}>홍보물 검토</h2>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {chip(`확인 ${okCount}`, OK_COLOR)}
          {chip(`수정 요청 ${fixCount}`, FIX_COLOR)}
          {chip(`아직 ${keyList.length - okCount - fixCount}`, MUTED)}
        </div>
        {youtubeState?.is_configured && (
          <button
            type="button"
            onClick={connectYoutube}
            style={{
              marginLeft: "auto",
              padding: "6px 14px",
              borderRadius: "999px",
              border: `1px solid ${youtubeState.channel ? LINE : "#E62117"}`,
              background: youtubeState.channel ? "#fff" : "#E62117",
              color: youtubeState.channel ? MUTED : "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              cursor: "pointer",
            }}
          >
            {youtubeState.channel ? "유튜브 연결됨 · 다시 연결" : "유튜브 채널 연결"}
          </button>
        )}
      </div>

      {sectionHead("상담 페이지")}
      <div style={gridStyle("wide")}>
        {data.link_list.map((link) => (
          <LinkCard key={link.key} link={link} review={reviewOf(link.key)} onSave={saveReview(link.key)} />
        ))}
      </div>

      {data.day_list.map((day) => (
        <section key={day.date}>
          {sectionHead(formatDay(day.date))}
          <ul style={{ margin: "0 0 4px", paddingLeft: "18px", fontSize: "0.9rem", lineHeight: 1.7 }}>
            {day.log_list.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {day.pending && (
            <p
              style={{
                margin: "10px 0 0",
                padding: "10px 12px",
                border: `1px dashed ${FIX_COLOR}`,
                borderRadius: "8px",
                fontSize: "0.88rem",
              }}
            >
              <b style={{ color: FIX_COLOR }}>대기</b> {day.pending}
            </p>
          )}
          {day.group_list.map((group) => (
            <div key={group.label}>
              <h4 style={{ fontSize: "0.85rem", color: MUTED, fontWeight: 700, margin: "20px 0 10px" }}>
                {group.label}
              </h4>
              <div style={gridStyle(group.shape)}>
                {group.item_list.map((item) => (
                  <VideoCard
                    key={item.key}
                    item={item}
                    shape={group.shape}
                    review={reviewOf(item.key)}
                    onSave={saveReview(item.key)}
                    onDelete={deleteItem(item.key)}
                    upload={youtubeState?.upload_map?.[item.key]}
                    canUpload={Boolean(youtubeState?.channel)}
                    onUpload={uploadToYoutube(item.key)}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};

export default PromoReviewPage;
